import { useState, useCallback } from 'react';

const useApi = (baseUrl) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Helper function to handle the API request
  const makeRequest = useCallback(async (url, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseUrl}${url}`, {
        ...options,
        credentials: 'include',
        headers: {
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const result = await response.json();
      setData(result);
      return result;
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  // POST request to handle form data
  const postFormData = useCallback((url, formData) => {
    const formBody = new FormData();
    Object.keys(formData).forEach((key) => {
      formBody.append(key, formData[key]);
    });

    return makeRequest(url, {
      method: 'POST',
      body: formBody,
    });
  }, [makeRequest]);

  // GET request
  const get = useCallback((url) => makeRequest(url, { method: 'GET' }), [makeRequest]);

  // POST request
  const post = useCallback((url, body) => makeRequest(url, { method: 'POST', body: JSON.stringify(body) }), [makeRequest]);

  // PUT request
  const put = useCallback((url, body) => makeRequest(url, { method: 'PUT', body: JSON.stringify(body) }), [makeRequest]);

  // PATCH request
  const patch = useCallback((url, body) => makeRequest(url, { method: 'PATCH', body: JSON.stringify(body) }), [makeRequest]);

  return {
    data,
    error,
    loading,
    get,
    post,
    put,
    patch,
    postFormData,
  };
};

export default useApi;
